'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import styles from './Testimonials.module.scss'
import { ComponentProps, RepeaterField } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'

interface TestimonialItem extends RepeaterField {
  quote?: string
  client?: string
}

interface TestimonialsProps extends ComponentProps {
  title?: string
  testimonials?: TestimonialItem[]
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

export const Testimonials: React.FC<TestimonialsProps> = ({
  title = 'What Our Clients Say',
  testimonials = [],
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const testimonialsArray = Array.isArray(testimonials) ? testimonials : []
  const [titleInView, setTitleInView] = useState(false)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad
  


  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current

    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]

        if (entry.isIntersecting && canAnimate) {

          setTitleInView(true)
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Hook for individual testimonial animations
  const useTestimonialAnimation = (index: number) => {
    const [inView, setInView] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const element = ref.current
      
      if (!element || isAdminMode) {
        if (isAdminMode) setInView(true)
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry.isIntersecting && canAnimate) {

            setInView(true)
          }
        },
        { threshold: 0.2 }
      )

      observer.observe(element)
      return () => observer.unobserve(element)
    }, [index])

    return { ref, shouldAnimate: inView }
  }

  return (
    <section className={styles.testimonials}>
      <div className={`${styles.testimonials__container} ${
        noTopPadding && noBottomPadding ? styles['testimonials__container--no-vertical'] :
        noTopPadding ? styles['testimonials__container--no-top'] :
        noBottomPadding ? styles['testimonials__container--no-bottom'] : ''
      }`}>
        <motion.h2 
          ref={titleRef}
          className={styles.testimonials__title}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: titleInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
        />
        
        <div className={styles.testimonials__grid}>
          {testimonialsArray.map((testimonial, index) => {
            const TestimonialItem = () => {
              const { ref, shouldAnimate } = useTestimonialAnimation(index)
              
              return (
                <div ref={ref} className={styles.testimonials__item}>
                  <motion.cite 
                    className={styles.testimonials__client}
                    initial={{ opacity: isAdminMode ? 1 : 0, y: isAdminMode ? 0 : 20 }}
                    animate={{ 
                      opacity: shouldAnimate ? 1 : 0,
                      y: shouldAnimate ? 0 : 20
                    }}
                    transition={{ 
                      duration: 0.8,
                      delay: index * 0.2,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(testimonial.client || '') }}
                  />
                  <motion.blockquote 
                    className={styles.testimonials__quote}
                    initial={{ opacity: isAdminMode ? 1 : 0, y: isAdminMode ? 0 : 30 }}
                    animate={{ 
                      opacity: shouldAnimate ? 1 : 0,
                      y: shouldAnimate ? 0 : 30
                    }}
                    transition={{ 
                      duration: 0.8,
                      delay: index * 0.2 + 0.3, // Client first, then quote 0.3s later
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(`&ldquo;${testimonial.quote || ''}&rdquo;`) }}
                  />
                </div>
              )
            }
            
            return <TestimonialItem key={testimonial.id} />
          })}
        </div>
      </div>
    </section>
  )
} 